import { useContext, useEffect, useState } from 'react';
import Button from '../../ui/Button';
import Dropdown from '../../ui/Dropdown';
import Heading from '../../ui/Heading';
import Input from '../../ui/Input';
import { BountiesContext } from '../../contexts/BountiesContext';
import RadioButtonComponent from '../../ui/RadioButtonComponent';
import { useBountyFields } from '../../hooks/useBountyFields';
import RankBasedDistributions from './RankBasedDistributions';
import Checkbox from '../../ui/Checkbox';
import { coresData, coresWithIconData, SUSTAINABLE_DEVELOPMENT_GOALS } from '../../constants/datas';
import { useStepSixInputs } from './bountiesHooks/useStepSixInputs';
import Toggle from '../../ui/Toggle';
import CharacterLimit from '../../ui/CharacterLimit';
import Loader from '../../ui/Loader';
import { getICImageOnBountyCreationScreen, getImpactPoints } from '../../apis/general/general';
import TextWithTooltip from '../../ui/TextWithTooltip';
import { useWatch } from 'react-hook-form';
import FloatingButtons from '../../ui/FloatingButtons';
import DatePicker from 'react-datepicker';
import StepFour from './StepFour';
import StepFourForLandscapes from './StepFourForLandscapes';
import { useStepFourInputs } from './bountiesHooks/useStepFourInputs';
import celoImg from '../../assets/chains/celo.png';
import optimismImg from '../../assets/chains/optimism.png';
import solanaImg from '../../assets/chains/solana.png';
import baseImg from '../../assets/chains/base.png';
import arbitrumImg from '../../assets/chains/arbitrum.png';
import NotSelectedIcon from '../../assets/icons/NotSelectedIcon';
import ButtonNew from '../../ui/ButtonNew';
import { N } from 'ethers';

const MAX_SELECTION = 5;

const CHAIN_OPTIONS = ['celo', 'optimism', 'solana', 'base', 'arbitrum'];

// Helper function to construct network options from tokens
const constructNetworkOptions = (tokens) => {
    if (!tokens || tokens.length === 0) return [];

    // Extract unique chains from tokens
    const uniqueChains = new Map();

    tokens.forEach(token => {
        if (token.chainImage && token.chainName) {
            // Normalize chain name (trim whitespace and convert to lowercase for key)
            const normalizedKey = token.chainName.trim().toLowerCase();

            // Only add if not already exists (first occurrence wins)
            if (!uniqueChains.has(normalizedKey)) {
                uniqueChains.set(normalizedKey, {
                    chainName: token.chainName.trim(),
                    chainImage: token.chainImage
                });
            }
        }
    });

    return Array.from(uniqueChains.values());
};

// Helper function to get chain image
const getChainImage = (chainName) => {
    switch (chainName) {
        case 'celo':
            return celoImg;
        case 'optimism':
            return optimismImg;
        case 'solana':
            return solanaImg;
        case 'base':
            return baseImg;
        case 'arbitrum':
            return arbitrumImg;
        default:
            return null;
    }
};

function StepSix({
    setStep,
    register,
    control,
    errors,
    getValues,
    setError,
    clearErrors,
    onSubmit,
    loading,
    reset,
    icImage,
    setIcImage,
    impactPointsData,
    isGreenpillEvent,
    setImpactPointsData,
    startGettingIcImage,
    stopGettingIcImage,
    eventNameFromUrl,
    backerHandler,
    isValidate,
    validationHandler,
    onSaveProgress,
    saving,
}) {
    const [maxSDGError, setMaxSDGError] = useState('');
    const [filteredTokens, setFilteredTokens] = useState([]);
    const { expirationTimeField, dayField, hoursField, minutesField } = useStepFourInputs(control, isValidate);
    const {
        tokenField,
        tokens,
        totalRewardAmountField,
        noOfWinnersField,
        passThresholdField,
        hasImpactCertificateField,
        impactBriefField,
        coresField,
        SDGsField,
        isAttestableField,
        dominantSDGField,
        distributionModeField,
        ranksField,
        withRewardTokenField,
        appendRank,
        removeRank,
        validationTillDateField,
        daySubField,
        hoursSubField,
        selfFundingField,
        minutesSubField,
        mintingChainField,
        selectedNetworkField,
    } = useStepSixInputs(
        control,
        getValues()?.hasImpactCertificate,
        getValues,
        setError,
        clearErrors,
        isGreenpillEvent,
        expirationTimeField,
        getValues()?.country
    );

    // Construct network options dynamically from tokens
    const NETWORK_OPTIONS = constructNetworkOptions(tokens);
    console.log(getValues()?.country, getValues(), 'country kya aa rahi h yha ');

    const watchedRanks = useWatch({ name: 'ranks', control });

    useEffect(() => {
        // First Payload: For max amount and impact points
        let maxAmount = 0;
        const maxAmountPayload = {};
        if (distributionModeField.value === 'Distribute evenly between all winners') {
            if (noOfWinnersField.value) {
                maxAmount = Number(totalRewardAmountField.value) / noOfWinnersField.value;
            }
        } else if (distributionModeField.value === 'Rank based distribution') {
            const ranks = watchedRanks.filter((i) => i.amount).map((obj) => Number(obj.amount));
            maxAmount = Math.max(...ranks);
        }
        maxAmountPayload.amount = maxAmount;
        maxAmountPayload.symbol = tokenField?.value?.symbol === "INR" ? "₹" : tokenField?.value?.symbol;
        maxAmountPayload.bountyType = getValues('type');

        if (maxAmount && maxAmountPayload.bountyType && maxAmountPayload.symbol) {
            const fetchImpactPoints = async () => {
                try {
                    const data = await getImpactPoints(maxAmountPayload, tokenField.value?.symbol === "INR");
                    const sum = data?.impactPoints + data?.participatoryImpactPoints;
                    setImpactPointsData(sum);
                } catch (error) {
                    console.error(error);
                }
            };
            fetchImpactPoints();
        }

        // Second Payload: For IC image generation
        const icImagePayload = {
            coresArray: [...new Set(coresField.value || [])],
            briefMessage: impactBriefField.value,
            sdgsArray: [...new Set(SDGsField.value || [])],
            tokenSymbol: tokenField.value?.symbol === "INR" ? "₹" : tokenField.value?.symbol,
            amount: maxAmount,
        };
        const fetchIcImage = async (withReward) => {
            try {
                startGettingIcImage();
                const data = await getICImageOnBountyCreationScreen(icImagePayload, withReward, tokenField.value?.symbol === "INR");
                setIcImage(data);
            } catch (error) {
                console.error(error);
            } finally {
                stopGettingIcImage();
            }
        };

        if (!withRewardTokenField.value) {
            if (
                icImagePayload.coresArray.length > 0 &&
                icImagePayload.briefMessage &&
                icImagePayload.sdgsArray.length > 0
            )
                fetchIcImage(false);
        }
        if (withRewardTokenField.value) {
            if (
                icImagePayload.coresArray.length > 0 &&
                icImagePayload.briefMessage &&
                icImagePayload.tokenSymbol &&
                icImagePayload.sdgsArray.length > 0
            ) {
                fetchIcImage(true);
            } else {
                setIcImage({});
            }
        }
    }, [
        tokenField.value,
        totalRewardAmountField.value,
        distributionModeField.value,
        noOfWinnersField.value,
        watchedRanks,
        coresField.value,
        SDGsField.value,
        impactBriefField.value,
        withRewardTokenField.value,
        getValues,
    ]);

    useEffect(
        function () {
            coresField.onChange([...(coresField.value || []), getValues()?.dominantCore]);
        },
        [getValues()?.dominantCore]
    );
    useEffect(
        function () {
            if (getValues()?.dominantSDG) {
                const filteredValues = SDGsField?.value?.filter((sdg) => sdg !== getValues()?.dominantSDG);
                SDGsField.onChange([...filteredValues, getValues()?.dominantSDG]);
            } else SDGsField.onChange([...(SDGsField.value || [])]);
        },
        [getValues()?.dominantSDG]
    );

    // Filter tokens based on selected network
    useEffect(() => {
        console.log(selectedNetworkField?.value, "selectedNetworkField?.value", NETWORK_OPTIONS)
        if (selectedNetworkField?.value && tokens.length > 0) {
            const networkName = selectedNetworkField?.value?.chainName?.toLowerCase();

            // Filter tokens based on chainName or chain property
            const filtered = tokens.filter(token => {
                // Handle different chainName formats
                const tokenChainName = (token.chainName || token.chain || '').toLowerCase();

                // Map network names to match token data
                const chainMappings = {
                    'ethereum': ['ethereum', 'eth'],
                    'optimism': ['optimism'],
                    'arbitrum': ['arbitrum'],
                    'base': ['base'],
                    'solana': ['solana'],
                    'base sepolia': ['base sepolia'],
                    'optimism sepolia': ['optimism sepolia']
                };

                const mappedNames = chainMappings[networkName] || [networkName];
                return mappedNames.some(name => tokenChainName.includes(name));
            });

            setFilteredTokens(filtered);
        } else {
            setFilteredTokens([]);
        }
    }, [selectedNetworkField.value, tokens]);

    console.log(tokens, "these are the tokens coming")
    // Handle network selection
    const handleNetworkChange = (network) => {
        selectedNetworkField.onChange(network);
        // Reset token selection when network changes
        tokenField.onChange(null);
    };

    const handleUpdateOptions = (isSelected, item) => {
        const currentSelection = SDGsField.value;
        const updatedSelection = isSelected
            ? currentSelection.filter((val) => val !== item) // Remove item if deselected
            : [...currentSelection, item]; // Add item if selected

        if (updatedSelection.length > MAX_SELECTION) {
            setMaxSDGError(`You can only choose upto 4 secondary SDG’s`);
            return; // Prevent further updates
        }

        setMaxSDGError(''); // Clear error if within limit
        updateOptions(isSelected, item); // Call the original update logic
    };

    function updateOptions(value, item) {
        if (value) {
            const removeRoleArr = SDGsField?.value?.filter((role) => role !== item);
            SDGsField.onChange(removeRoleArr);
        } else {
            SDGsField.onChange([...(SDGsField?.value || []), item]);
        }
    }
    function updateCores(value, item) {
        if (value) {
            const removeRoleArr = coresField?.value?.filter((role) => role !== item);
            coresField.onChange(removeRoleArr);
        } else {
            coresField.onChange([...(coresField.value || []), item]);
        }
    }

    const tokenSymbols = tokens.map((token) => token.symbol);
    const hasError = errors?.daySub?.message || errors?.hoursSub?.message || errors?.minutesSub?.message ? true : false;
    const hasError2 = errors?.day?.message || errors?.hours?.message || errors?.minutes?.message ? true : false;

    const isSelfFunding = selfFundingField?.value === 'Yes, we will fund the bounty';
    console.log(withRewardTokenField, 'withRe');
    console.log(tokens, "populate these tokens", NETWORK_OPTIONS, tokenField);
    return (
        <div className="relative mb-48">
            <div className="p-12 pb-0  min-h-full flex flex-col justify-between  rounded-md ">
                <div>
                    {
                        <div className="mb-6">
                            <div className="flex gap-3">
                                <Heading type="xsmall" className="capitalize  text-textPrimary">
                                    Bounty Reward
                                </Heading>
                            </div>
                            <p className="text-sm text-textSecondary ">
                                Choose bounty reward token and set the amount
                            </p>
                        </div>
                    }
                    {!isGreenpillEvent && (
                        <div>
                            <div className="flex gap-2 items-center">
                                <Toggle
                                    valuee={withRewardTokenField.value}
                                    onChangeFn={() => {
                                        if (getValues()?.withRewardToken) {
                                            clearErrors('token');
                                            clearErrors('noOfWinners');
                                            clearErrors('totalRewardAmount');

                                            withRewardTokenField.onChange(false);
                                            return;
                                        }
                                        if (!getValues()?.withRewardToken) withRewardTokenField.onChange(true);
                                    }}
                                />
                                <Heading type="xsmall" className="  text-textPrimary ">
                                    Reward Token
                                </Heading>
                            </div>
                            <p className="text-sm text-textSecondary mt-1">
                                Choose bounty reward token and set the amount
                            </p>
                        </div>
                    )}

                    {isGreenpillEvent && (
                        <div className="my-6 py-4 px-5 bg-grayBgNormal border rounded-md border-borderUnderline">
                            <Heading type="xsmall" className="capitalize  text-textPrimary">
                                Choose Funding Type
                            </Heading>
                            <p className="text-sm text-textSecondary">
                                You can fund a bounty yourself or via Impact Landscapes, a platform designed for backers
                                who want to fund bounties
                            </p>
                            <RadioButtonComponent
                                className={'flex-col mt-2'}
                                selectedOption={selfFundingField.value}
                                onSelectOption={(value) => {
                                    selfFundingField.onChange(value);
                                    if (value === 'Yes, we will fund the bounty') {
                                        backerHandler(true);
                                        validationHandler(true);
                                    }
                                    if (value === 'No, we want funding via Impact Landscapes') {
                                        backerHandler(false);
                                        validationHandler(false);
                                    }
                                }}
                                options={['Yes, we will fund the bounty', 'No, we want funding via Impact Landscapes']}
                            />
                        </div>
                    )}

                    {!isGreenpillEvent && (
                        <div className="my-6">
                            <div className="flex gap-3">
                                <Heading type="xsmall" className="capitalize  text-textPrimary">
                                    Validation
                                </Heading>
                            </div>
                            <p className="text-sm text-textSecondary ">
                                For bounties WITH a reward token- all submission have to be graded together
                            </p>
                            <p className="text-sm text-textSecondary ">
                                For bounties WITHOUT a reward token- submissions have to be graded individually
                            </p>
                        </div>
                    )}
                    {selfFundingField?.value === 'Yes, we will fund the bounty' && (
                        <div>
                            <div className="relative">
                                <div className=" pb-0 flex flex-col justify-between min-h-full h-  rounded-md ">
                                    <div>
                                        <div className="mb-6">
                                            <div className="flex gap-3">
                                                <TextWithTooltip
                                                    heading={
                                                        <p className="text-textPrimary">
                                                            Once published, how long should the bounty be live?{' '}
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </p>
                                                    }
                                                >
                                                    <p className="text-sm">
                                                        This is the bounty expiration time after which it will disappear
                                                        from Impact Miner and users will not be able to participate
                                                    </p>
                                                </TextWithTooltip>
                                            </div>

                                            <div>
                                                <DatePicker
                                                    showTimeSelect
                                                    minDate={new Date()}
                                                    className="border border-borderUnderline w-[230px] shadow-sm text-sm bg-grayBgNormal placeholder:text-textTertiary font-semibold rounded-md px-4 py-2 focus:outline-none focus:border-blueNormal focus:ring-0"
                                                    dayClassName={(date) => {
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        const compareDate = new Date(date);
                                                        compareDate.setHours(0, 0, 0, 0);

                                                        // Only apply disabled styling to past dates, not future dates from next month
                                                        if (compareDate < today) {
                                                            return 'force-disabled-date';
                                                        }
                                                        return '';
                                                    }}
                                                    // minTime={new Date()}
                                                    // maxTime={new Date(0, 0, 0, 19, 0)}
                                                    placeholderText="Set Date & Time"
                                                    selected={
                                                        expirationTimeField?.value instanceof Date &&
                                                            !isNaN(expirationTimeField?.value)
                                                            ? expirationTimeField?.value
                                                            : null
                                                    }
                                                    onChange={(date) => {
                                                        expirationTimeField.onChange(date);

                                                        // Reset validation deadline if expiry date is set after it
                                                        const validationDate = validationTillDateField?.value;
                                                        if (date && validationDate &&
                                                            date instanceof Date && validationDate instanceof Date &&
                                                            !isNaN(date) && !isNaN(validationDate) &&
                                                            date >= validationDate) {
                                                            validationTillDateField.onChange(null);
                                                        }
                                                    }}
                                                    dateFormat="MMMM d, yyyy h:mmaa"
                                                    timeIntervals={1}

                                                // customInput={<CustomDateInput placeholder="Set Date & Time" className="bg-grayBgNormal" />}
                                                />
                                                {errors.expirationTime && (
                                                    <p className="text-foundryRed">{errors.expirationTime.message}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="my-6">
                                            <div className="flex gap-3">
                                                <TextWithTooltip
                                                    heading={
                                                        <p className="text-textPrimary">
                                                            Estimated time to complete bounty{' '}
                                                            <sup className="text-lg relative top-[1px]">*</sup>
                                                        </p>
                                                    }
                                                >
                                                    <p className="text-sm">
                                                        An estimation of time will help users on Impact Miner understand
                                                        how long it will take to complete the bounty
                                                    </p>
                                                </TextWithTooltip>
                                            </div>
                                            <div className="flex w-[50%] mt-1 gap-1">
                                                <div>
                                                    <div>
                                                        <Input
                                                            {...dayField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p className="">day</p>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div>
                                                        <Input
                                                            {...hoursField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p>hours</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div>
                                                        <Input
                                                            {...minutesField}
                                                            className="w-full text-sm font-semibold"
                                                            type="number"
                                                            placeholder={0}
                                                        />
                                                        <p>minutes</p>
                                                    </div>
                                                </div>
                                            </div>
                                            {hasError2 && (
                                                <p className="text-foundryRed">{'Please enter the estimated time'}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* <FloatingButtons>
                <Button type="button" onClick={() => setStep(3)} className="bg-grayBgNormal" variant="tertiary" size="small">
                    Back
                </Button>
                <Button type="button" onClick={() => setStep(5)} size="small">
                    Next
                </Button>
            </FloatingButtons> */}
                            </div>
                        </div>
                    )}
                    {selfFundingField?.value === 'Yes, we will fund the bounty' && (
                        <div>
                            {(isGreenpillEvent ? true : withRewardTokenField?.value) ? (
                                <div className="mb-6">
                                    <div className="flex gap-3">
                                        <TextWithTooltip
                                            heading={
                                                <p className="text-textPrimary">
                                                    Set Deadline for validating all submissions{' '}
                                                    <sup className="text-lg relative top-[1px]">*</sup>
                                                </p>
                                            }
                                        >
                                            <p className="text-sm">
                                                Validation determines the score of each submission and in turn the
                                                winners.
                                            </p>
                                            <p className="text-sm mt-3">
                                                Setting a deadline will inform users how long it will take for the
                                                results to be announced
                                            </p>
                                        </TextWithTooltip>
                                    </div>
                                    <div>
                                        <DatePicker
                                            minDate={new Date(getValues('expirationTime'))}
                                            showTimeSelect
                                            className="border border-borderUnderline w-[230px] shadow-sm text-sm bg-grayBgNormal placeholder:text-textTertiary font-semibold rounded-md px-4 py-2 focus:outline-none focus:border-blueNormal focus:ring-0"
                                            placeholderText="Set Date & Time"
                                            selected={
                                                validationTillDateField?.value instanceof Date &&
                                                    !isNaN(validationTillDateField?.value)
                                                    ? validationTillDateField?.value
                                                    : null
                                            }
                                            onChange={(date) => {
                                                validationTillDateField.onChange(date);
                                            }}
                                            dateFormat="MMMM d, yyyy h:mmaa"
                                            timeIntervals={15}
                                        />
                                        {errors.validationTillDate && (
                                            <p className="text-foundryRed">{errors.validationTillDate.message}</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="my-6">
                                    <div className="flex gap-3">
                                        <TextWithTooltip
                                            heading={
                                                <p className="text-textPrimary">
                                                    Set Deadline for validation time after each submission
                                                    <sup className="text-lg relative top-[1px]">*</sup>
                                                </p>
                                            }
                                        >
                                            <p className="text-sm">
                                                Validation determines the score of each submission and in turn the
                                                winners.
                                            </p>
                                            <p className="text-sm mt-3">
                                                Setting a deadline will inform users how long it will take for the
                                                results to be announced
                                            </p>
                                        </TextWithTooltip>
                                    </div>
                                    <div className="flex w-[50%] mt-1 gap-1">
                                        <div>
                                            <div>
                                                <Input
                                                    {...daySubField}
                                                    className="w-full text-sm font-semibold"
                                                    type="number"
                                                    placeholder={0}
                                                />
                                                <p className="">day</p>
                                            </div>
                                        </div>

                                        <div>
                                            <div>
                                                <Input
                                                    {...hoursSubField}
                                                    className="w-full text-sm font-semibold"
                                                    type="number"
                                                    placeholder={0}
                                                />
                                                <p>hours</p>
                                            </div>
                                        </div>
                                        <div>
                                            <div>
                                                <Input
                                                    {...minutesSubField}
                                                    className="w-full text-sm font-semibold"
                                                    type="number"
                                                    placeholder={0}
                                                />
                                                <p>minutes</p>
                                            </div>
                                        </div>
                                    </div>
                                    {hasError && (
                                        <p className="text-foundryRed">{'Please enter the validation deadline time'}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {(isGreenpillEvent ? true : withRewardTokenField?.value) && (
                        <div className="flex flex-col  mb-6 ">
                            <div className="w-full">
                                <p className="text-textPrimary">
                                    Reward Token & Budget for this bounty?{' '}
                                    <sup className="text-lg relative top-[1px]">*</sup>{' '}
                                </p>

                                {/* Network, Token, and Amount in a row */}


                                {
                                    getValues()?.country === 'IN' ? (
                                        <div className="grid grid-cols-5  mt-2 w-full items-start">
                                            {/* Network Dropdown */}



                                            {/* Token Dropdown */}
                                            <div className="flex flex-col col-span-1 relative">

                                                <Dropdown
                                                    isFiat={true}
                                                    modifyLabel={(token) => (
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                className="w-5 h-5 rounded-full"
                                                                src={token?.image}
                                                                alt={token?.name}
                                                            />
                                                            <span>{token?.symbol}</span>
                                                        </div>
                                                    )}
                                                    modifySelected={(token) => (
                                                        <div className="flex items-center gap-2">
                                                            <span>₹{token?.symbol}</span>
                                                        </div>
                                                    )}
                                                    data={["INR"]}
                                                    btnClass='rounded-e-none'
                                                    selected={tokenField.value}
                                                    onSelected={tokenField.onChange}
                                                    noSelectedText={
                                                        <span className="flex items-center gap-[7px]">
                                                            <NotSelectedIcon /> <span>Choose Token</span>
                                                        </span>
                                                    }
                                                    w="w-full"
                                                    disabled={true}
                                                />
                                                {/* Token validation message */}
                                                {errors?.token?.message && (
                                                    <p className="text-foundryRed text-sm mt-1">{errors.token.message}</p>
                                                )}
                                            </div>

                                            {/* Amount Input */}
                                            <div className="flex flex-col col-span-4 relative">

                                                <input
                                                    {...totalRewardAmountField}
                                                    type="number"
                                                    min="0"
                                                    step=".000000001"
                                                    placeholder="Amount"
                                                    onWheel={(event) => event.target.blur()}
                                                    className="border bg-primaryBg border-primaryBorder disabled:bg-[#F5F5F5] text-textPrimary placeholder:text-textSecondary rounded-lg rounded-s-none px-3 py-2 text-sm w-full"
                                                />
                                                {/* Amount validation message */}
                                                {errors?.totalRewardAmount?.message && (
                                                    <p className="text-foundryRed text-sm mt-1">{errors.totalRewardAmount.message}</p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-3 mt-2 w-full items-start">
                                            {/* Network Dropdown */}

                                            <div className="flex flex-col relative">
                                                <label className="text-sm text-textPrimary mb-1">Network</label>
                                                <Dropdown
                                                    modifyLabel={(network) => (
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                className="w-5 h-5 rounded-full"
                                                                src={network?.chainImage}
                                                                alt={network?.chainName}
                                                            />
                                                            <span className='text-textPrimary' style={{ textTransform: 'capitalize' }}>{network?.chainName}</span>
                                                        </div>
                                                    )}
                                                    modifySelected={(network) => (
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                className="w-5 h-5 rounded-full"
                                                                src={network?.chainImage}
                                                                alt={network?.chainName}
                                                            />
                                                            <span style={{ textTransform: 'capitalize' }}>{network?.chainName}</span>
                                                        </div>
                                                    )}
                                                    data={NETWORK_OPTIONS}
                                                    selected={selectedNetworkField.value}
                                                    onSelected={handleNetworkChange}
                                                    noSelectedText={
                                                        <span className="flex items-center gap-[7px]">
                                                            <NotSelectedIcon /> <span>Choose Network</span>
                                                        </span>
                                                    }
                                                    w="w-full"
                                                />
                                            </div>

                                            {/* Token Dropdown */}
                                            <div className="flex flex-col relative">
                                                <label className="text-sm text-textPrimary mb-1">Token</label>
                                                <Dropdown
                                                    modifyLabel={(token) => (
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                className="w-5 h-5 rounded-full"
                                                                src={token?.image}
                                                                alt={token?.name}
                                                            />
                                                            <span className='text-textPrimary'>{token?.symbol}</span>
                                                        </div>
                                                    )}
                                                    modifySelected={(token) => (
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                className="w-5 h-5 rounded-full"
                                                                src={token?.image}
                                                                alt={token?.name}
                                                            />
                                                            <span>{token?.symbol}</span>
                                                        </div>
                                                    )}
                                                    data={filteredTokens}
                                                    selected={tokenField.value}
                                                    onSelected={tokenField.onChange}
                                                    noSelectedText={
                                                        <span className="flex items-center gap-[7px]">
                                                            <NotSelectedIcon /> <span>Choose Token</span>
                                                        </span>
                                                    }
                                                    w="w-full"
                                                    disabled={!selectedNetworkField.value}
                                                />
                                                {/* Token validation message */}
                                                {errors?.token?.message && (
                                                    <p className="text-foundryRed text-sm mt-1">{errors.token.message}</p>
                                                )}
                                            </div>

                                            {/* Amount Input */}
                                            <div className="flex flex-col relative">
                                                <label className="text-sm text-textPrimary mb-1">Amount</label>
                                                <input
                                                    {...totalRewardAmountField}
                                                    type="number"
                                                    min="0"
                                                    step=".000000001"
                                                    placeholder="Amount"
                                                    onWheel={(event) => event.target.blur()}
                                                    className="border bg-primaryBg border-primaryBorder disabled:bg-[#F5F5F5] text-textPrimary placeholder:text-textSecondary rounded-lg px-3 py-2 text-sm w-full"
                                                />
                                                {/* Amount validation message */}
                                                {errors?.totalRewardAmount?.message && (
                                                    <p className="text-foundryRed text-sm mt-1">{errors.totalRewardAmount.message}</p>
                                                )}
                                            </div>
                                        </div>
                                    )
                                }


                            </div>
                        </div>
                    )}

                    {selfFundingField?.value === 'No, we want funding via Impact Landscapes' && (
                        <div>
                            <StepFourForLandscapes control={control} errors={errors} />
                        </div>
                    )}
                    {(isGreenpillEvent ? true : withRewardTokenField?.value) && (
                        <div className="mb-6">
                            <p className="text-textPrimary">
                                How many winners ? <sup className="text-lg relative top-[1px]">*</sup>
                            </p>
                            <Input
                                {...noOfWinnersField}
                                type="number"
                                error={errors?.noOfWinners?.message}
                                onChange={(e) => {
                                    // if (distributionModeField.value === 'Rank based distribution') {
                                    const count = parseInt(e.target.value, 10);
                                    if (!isNaN(count) && count >= 0) {
                                        // Clear existing fields if needed
                                        const currentFields = ranksField.length;

                                        // If the new count is greater than the current count, append fields
                                        for (let i = currentFields; i < count; i++) {
                                            appendRank({ value: '' });
                                        }

                                        // If the new count is less than the current count, remove fields
                                        for (let i = currentFields; i > count; i--) {
                                            removeRank(i - 1);
                                        }
                                        // }
                                    }
                                    noOfWinnersField.onChange(e.target.value);
                                }}
                                placeholder={'Enter number of winners'}
                                className={'w-[100%] mt-2'}
                            />
                        </div>
                    )}
                    {(isGreenpillEvent ? true : withRewardTokenField?.value) && (
                        <div className="mb-6">
                            <p className="text-textPrimary">How would you like to distribute the rewards ?</p>
                            <RadioButtonComponent
                                className={'flex-col mt-2'}
                                selectedOption={distributionModeField.value}
                                onSelectOption={distributionModeField.onChange}
                                options={['Distribute evenly between all winners', 'Rank based distribution']}
                            />
                        </div>
                    )}

                    {(isGreenpillEvent ? true : withRewardTokenField?.value)
                        ? distributionModeField.value === 'Rank based distribution' && (
                            <RankBasedDistributions
                                errors={errors}
                                register={register}
                                ranksField={ranksField}
                                control={control}
                            />
                        )
                        : null}
                    {(isGreenpillEvent ? true : withRewardTokenField?.value) && (
                        <div className="mb-6">
                            <p className="mt-2 text-textPrimary">
                                Maximum Impact Points allocated:{' '}
                                <span className="font-bold">{impactPointsData ? impactPointsData : null}</span>{' '}
                            </p>
                        </div>
                    )}
                    <div className="mb-6">
                        <TextWithTooltip
                            heading={
                                <p className="text-textPrimary">
                                    Set Pass Threshold <sup className="text-lg relative top-[1px]">*</sup>
                                </p>
                            }
                        >
                            <p className="text-sm">
                                Determines the cut off accuracy for the user to be eligible to win.
                            </p>
                            <p className="text-sm mt-3 ">
                                ex, if 50% is chosen as the Pass Threshold. A users with 40% accuracy upon validation
                                will not be eligible to be a winner.
                            </p>
                        </TextWithTooltip>
                        <Input
                            {...passThresholdField}
                            type="number"
                            step="0.002"
                            error={errors?.passThreshold?.message}
                            placeholder={'Enter the pass %'}
                            className={'w-[100%] mt-2'}
                        />
                    </div>
                    <div className=" mb-6">
                        <div className="flex gap-2 items-center">
                            <Toggle
                                valuee={hasImpactCertificateField.value}
                                onChangeFn={() => {
                                    if (getValues()?.hasImpactCertificate) {
                                        clearErrors('impactBrief');
                                        hasImpactCertificateField.onChange(false);
                                        return;
                                    }
                                    if (!getValues()?.hasImpactCertificate) hasImpactCertificateField.onChange(true);
                                }}
                            />
                            <Heading type="xsmall" className="capitalize  text-textPrimary">
                                Impact Certificate
                            </Heading>
                        </div>
                        <p className="text-sm text-textSecondary">
                            Do you wish to issue impact certificates for this bounty?
                        </p>
                    </div>
                    {/* {hasImpactCertificateField.value && (
                        <div className="mb-6">
                            <TextWithTooltip
                                heading={
                                    <p className="text-textPrimary">
                                        Impact Certificate Brief <sup className="text-lg relative top-[1px]">*</sup>
                                    </p>
                                }
                            >
                                <p className="text-sm">
                                    Briefly describe the impact created by the user by doing the bounty
                                </p>
                            </TextWithTooltip>

                            <Input
                                {...impactBriefField}
                                type="textArea"
                                name="impactField"
                                placeholder={'Enter a brief description'}
                                className={'w-[100%]'}
                                error={errors?.impactBrief?.message}
                            />
                            <CharacterLimit min={impactBriefField.value?.length} max={100} />
                        </div>
                    )} */}
                    {hasImpactCertificateField.value && (
                        <>
                            <div className="mb-6">
                                <TextWithTooltip
                                    heading={
                                        <p className="text-textPrimary">
                                            Chain for Minting <sup className="text-lg relative top-[1px]">*</sup>
                                        </p>
                                    }
                                >
                                    <p className="text-sm">
                                        Select the chain on which users can mint the Impact Certificate
                                    </p>
                                </TextWithTooltip>

                                <div className="relative mt-2">
                                    <Dropdown
                                        data={CHAIN_OPTIONS}
                                        selected={mintingChainField.value}
                                        onSelected={(value) => mintingChainField.onChange(value)}
                                        noSelectedText="Select chain"
                                        btnClass={`${false
                                            ? 'bg-[#333333] border-[#3A3F49] text-white'
                                            : 'bg-grayBgNormal border-[#E5E5E5] shadow-[0_0_0_0_rgba(0,0,0,0.1)]'
                                            }`}
                                        modifyLabel={(chainName) => (
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={getChainImage(chainName)}
                                                    alt={chainName}
                                                    className="w-[18px] h-[18px] rounded-[200px]"
                                                />
                                                <span className="capitalize text-textPrimary">{chainName}</span>
                                            </div>
                                        )}
                                        modifySelected={(selected) => (
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={getChainImage(selected)}
                                                    alt={selected}
                                                    className="w-[18px] h-[18px] rounded-[200px]"
                                                />
                                                <span>{selected}</span>
                                            </div>
                                        )}
                                        error={errors?.impactCertificateChainName?.message}
                                    />
                                </div>
                            </div>

                            <div className="mb-6">
                                <TextWithTooltip
                                    heading={
                                        <p className="text-textPrimary">
                                            Impact Certificate Brief <sup className="text-lg relative top-[1px]">*</sup>
                                        </p>
                                    }
                                >
                                    <p className="text-sm">
                                        Briefly describe the impact created by the user by doing the bounty
                                    </p>
                                </TextWithTooltip>

                                <Input
                                    {...impactBriefField}
                                    type="textArea"
                                    name="impactField"
                                    placeholder={'Enter a brief description'}
                                    className={'w-[100%]'}
                                    error={errors?.impactBrief?.message}
                                />
                                <CharacterLimit min={impactBriefField.value?.length} max={100} />
                            </div>
                        </>
                    )}
                    <div className="mb-6">
                        <TextWithTooltip
                            heading={
                                <Heading type="xsmall" className="capitalize  text-textPrimary">
                                    Additional Impact Cores
                                </Heading>
                            }
                        >
                            <p className="text-sm">
                                You have previously chosen a dominant impact core. Here you can add more impact cores
                                which would be displayed in the impact certificate.
                            </p>
                        </TextWithTooltip>
                        <div className="relative">
                            <Dropdown
                                w="w-full"
                                data={[
                                    getValues()?.dominantCore,
                                    ...coresData.filter((core) => core !== getValues()?.dominantCore),
                                ]}
                                modifySelected={(array) => {
                                    return array.map((text) => (
                                        <span className="flex items-center gap-1">
                                            <img
                                                className={` ${text.toLowerCase() === 'water' || text.toLowerCase() === 'social'
                                                    ? 'w-4 mr-1'
                                                    : 'w-5 h-5'
                                                    }  `}
                                                src={
                                                    coresWithIconData?.find(
                                                        (obj) => obj.label.toLowerCase() === text.toLowerCase()
                                                    )?.icon
                                                }
                                                alt="image"
                                            />
                                            <span>{text}</span>
                                        </span>
                                    ));
                                }}
                                selected={[...new Set(coresField?.value || [])]}
                                render={(listItem) => {
                                    return (
                                        <Checkbox
                                            key={listItem + Math.random()}
                                            pl="pl-5"
                                            name={listItem}
                                            value={coresField?.value?.includes(listItem)}
                                            disabling={listItem === getValues()?.dominantCore}
                                            label={listItem}
                                            icon={
                                                <img
                                                    className={` ${listItem.toLowerCase() === 'water' ||
                                                        listItem.toLowerCase() === 'social'
                                                        ? 'w-4 mr-1'
                                                        : 'w-5 h-5'
                                                        }  `}
                                                    src={
                                                        coresWithIconData?.find(
                                                            (obj) => obj.label.toLowerCase() === listItem.toLowerCase()
                                                        )?.icon
                                                    }
                                                    alt="image"
                                                />
                                            }
                                            update={() => updateCores(coresField?.value?.includes(listItem), listItem)}
                                        />
                                    );
                                }}
                                withCheckBox={true}
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="mb-6">
                            <TextWithTooltip
                                heading={
                                    <Heading type="xsmall" className="capitalize mb-2 text-textPrimary">
                                        SDGs <sup className="text-lg relative top-[1px]">*</sup>
                                    </Heading>
                                }
                            >
                                <p className="text-sm">Choose the SDGs based on the impact areas of bounty.</p>
                                <p className="mt-3 text-sm">
                                    Sustainable development goals is a framework created by the UN that we're using to
                                    measure impact. You can learn more about it here:{' '}
                                    <a className="text-foundryBlue" href="https://sdgs.un.org/goals" target="_blank">
                                        https://sdgs.un.org/goals
                                    </a>
                                </p>
                            </TextWithTooltip>

                            <div className="relative mb-2">
                                <Dropdown
                                    data={SUSTAINABLE_DEVELOPMENT_GOALS}
                                    selected={dominantSDGField.value}
                                    onSelected={dominantSDGField.onChange}
                                    noSelectedText="Choose Dominant SDG"
                                    error={errors?.dominantSDG?.message}
                                />
                            </div>
                            <div className="relative mb-28">
                                <Dropdown
                                    w="w-full"
                                    data={[
                                        getValues()?.dominantSDG,
                                        ...SUSTAINABLE_DEVELOPMENT_GOALS.filter(
                                            (SDG) => SDG !== getValues()?.dominantSDG
                                        ),
                                    ]}
                                    selected={SDGsField?.value}
                                    noSelectedText="Choose other SDGs"
                                    render={(listItem) => {
                                        return (
                                            <Checkbox
                                                pl="pl-5"
                                                name={listItem}
                                                value={SDGsField?.value?.includes(listItem)}
                                                disabling={listItem === getValues()?.dominantSDG}
                                                label={listItem}
                                                update={() =>
                                                    handleUpdateOptions(SDGsField?.value?.includes(listItem), listItem)
                                                }
                                            />
                                        );
                                    }}
                                    withCheckBox={true}
                                />
                                {maxSDGError && <p className="text-foundryRed mt-2">{maxSDGError}</p>}
                            </div>
                        </div>
                    </div>
                    {/* <div className=" mb-2">
                        <div className="flex gap-2 items-center">
                            <Toggle
                                valuee={isAttestableField.value}
                                onChangeFn={() => {
                                    if (getValues()?.isAttestable) {
                                        isAttestableField.onChange(false);
                                        return;
                                    }
                                    if (!getValues()?.isAttestable) isAttestableField.onChange(true);
                                }}
                            />
                            <TextWithTooltip
                                heading={
                                    <Heading type="xsmall" className="capitalize  text-textPrimary">
                                        Attestation
                                    </Heading>
                                }
                            >
                                <p className="text-sm">
                                    Attesters are those who verify the authenticity of the bounty validation, this is
                                    done to ensure rewards are given to the rightful winners. It's done using Etherium
                                    Attestation Services (EAS)
                                </p>
                            </TextWithTooltip>
                        </div>
                        <p className="text-sm text-textSecondary">Do you want this bounty to be attestable?</p>
                    </div> */}
                </div>
            </div>
            <FloatingButtons>
                <div className="flex items-center gap-3">
                    <ButtonNew
                        type="button"
                        fixedMinWidth={false}
                        variant="neutral"
                        className='min-w-16 text-neutral600'
                        size="sm"
                        onClick={() => setStep(3)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                            <path d="M1.25586 9.4986L8.14899 2.60547L9.26857 3.72505L4.2867 8.70693L17.4171 8.70693L17.4171 10.2903L4.2867 10.2903L9.26857 15.2721L8.14899 16.3917L1.25586 9.4986Z" fill="currentColor" />
                        </svg>
                    </ButtonNew>

                    <ButtonNew
                        type="button"
                        fixedMinWidth={true}
                        variant="neutral"
                        size="sm"
                        onClick={onSaveProgress}
                        disabled={saving}
                    >
                        {saving ? <Loader /> : 'Save as Draft'}
                    </ButtonNew>
                </div>

                {isSelfFunding ? (
                    <ButtonNew
                        type="button"
                        fixedMinWidth={true}
                        variant="blue"
                        size="sm"
                        onClick={() => setStep(5)}
                    >
                        Next
                    </ButtonNew>

                ) : (
                    <ButtonNew
                        type="submit"
                        disabled={loading}
                        fixedMinWidth={true}
                        variant="blue"
                        size="sm"

                    >
                        {loading ? <Loader /> : 'Request Funding'}
                    </ButtonNew>

                )}
            </FloatingButtons>
        </div>
    );
}

export default StepSix;
